import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../blocks/AnimatedSection';
import PartnerCard from '../blocks/PartnerCard';
import FacultyAdvisorCard from '../blocks/FacultyAdvisorCard';
import { TransdisciplinarityOverview } from '../blocks/TransdisciplinarityIntro';
import { getEventsForNews, getTimelineEntriesByYear, getPostersForDashboard } from '../../config/siteEvents';
import {
  getPartnersByKeys,
  STUDENT_LEARNING_PARTNER_KEYS,
  SUPPORTING_ORG_KEYS,
  MONGOLIA_PARTNER_KEYS,
} from '../../config/partners';
import { getAdvisorsByKeys, CORTEX_FACULTY_ADVISOR_KEYS } from '../../config/advisors';
import { getCortex2026KeynoteAdvisorKey, CORTEX_2026_KEYNOTE_FOOTER_BADGE } from '../../config/cortexEvents';
import { cortexOutlineButtonClass } from '../../config/themeColors';

// Event Poster Component
const EventPoster = ({ image, alt, link, fallbackGradient, title, subtitle }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <Link to={link} className="block w-full h-full hover:opacity-95 transition-opacity">
      <div className="w-full h-full relative">
        {!imageError ? (
          <img
            src={image}
            alt={alt}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-r ${fallbackGradient} flex items-center justify-center`}>
            <div className="text-center text-white">
              <h3 className="text-2xl font-bold mb-2">{title}</h3>
              <p className="text-lg">{subtitle}</p>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

const CARD_GAP = 24;
const CARD_MIN_WIDTH = 320;

/** Truncate blurb to roughly 3 lines by word count and append ellipsis in the text (keeps justified alignment clean). */
const truncateBlurb = (text, maxWords = 30) => {
  if (!text || typeof text !== 'string') return '';
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(' ') + ' …';
};

const NewsSectionWithScroll = () => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const amount = (CARD_MIN_WIDTH + CARD_GAP) * (direction === 'left' ? -1 : 1);
    scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const events = getEventsForNews();

  return (
    <AnimatedSection
      className="py-16 md:py-20 bg-gray-50"
      heading={
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">News</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scroll('left')}
              aria-label="Scroll news left"
              className="p-2 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => scroll('right')}
              aria-label="Scroll news right"
              className="p-2 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      }
      containerClassName="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
    >
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto overflow-y-hidden pb-2 scroll-smooth snap-x snap-mandatory [&::-webkit-scrollbar]:hidden pt-4 px-4 sm:px-6 lg:px-8 -mx-4 sm:-mx-6 lg:-mx-8"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {events.map((event) => {
          const accent = event.accentColor || 'blue';
          const ringCls = { amber: 'hover:ring-amber-300', blue: 'hover:ring-blue-300', orange: 'hover:ring-orange-300', purple: 'hover:ring-purple-300', yellow: 'hover:ring-yellow-300' }[accent] || 'hover:ring-blue-300';
          const textCls = { amber: 'text-amber-600', blue: 'text-blue-600', orange: 'text-orange-600', purple: 'text-purple-600', yellow: 'text-yellow-600' }[accent] || 'text-blue-600';
          const titleHoverCls = { amber: 'group-hover:text-amber-700', blue: 'group-hover:text-blue-700', orange: 'group-hover:text-orange-700', purple: 'group-hover:text-purple-700', yellow: 'group-hover:text-yellow-700' }[accent] || 'group-hover:text-blue-700';
          return (
            <Link
              key={`${event.platform}-${event.slug}`}
              to={event.path}
              className={`group flex-shrink-0 w-[min(100%,320px)] md:w-80 p-6 rounded-2xl bg-white ring-1 ring-gray-200 transition shadow-sm block snap-start ${ringCls}`}
            >
              <p className={`text-xs uppercase tracking-wide font-semibold ${textCls}`}>
                {event.newsCategory}
              </p>
              <h3 className={`mt-2 text-lg font-semibold text-gray-900 ${titleHoverCls}`}>
                {event.title}
              </h3>
              <p className="mt-2 text-sm text-gray-600">{event.newsDateLabel}</p>
              <p className="mt-2 text-sm text-gray-700 text-justify break-words">{truncateBlurb(event.newsBlurb)}</p>
            </Link>
          );
        })}
      </div>
    </AnimatedSection>
  );
};

// TITAN theme colors based on the logo (module scope so init effects do not need theme array in deps)
const PROMOTIONAL_THEME_COLORS = [
  { bg: 'bg-blue-500/20', name: 'blue', heroBg: 'bg-gradient-to-br from-blue-100/80 via-blue-50/40 to-blue-100/80', fadeFrom: 'rgb(239 246 255)' },
  { bg: 'bg-green-500/20', name: 'green', heroBg: 'bg-gradient-to-br from-green-100/80 via-green-50/40 to-green-100/80', fadeFrom: 'rgb(240 253 244)' },
  { bg: 'bg-orange-500/20', name: 'orange', heroBg: 'bg-gradient-to-br from-orange-100/80 via-orange-50/40 to-orange-100/80', fadeFrom: 'rgb(255 247 237)' },
  { bg: 'bg-purple-500/20', name: 'purple', heroBg: 'bg-gradient-to-br from-purple-100/80 via-purple-50/40 to-purple-100/80', fadeFrom: 'rgb(250 245 255)' },
  { bg: 'bg-yellow-500/20', name: 'yellow', heroBg: 'bg-gradient-to-br from-yellow-100/80 via-yellow-50/40 to-yellow-100/80', fadeFrom: 'rgb(254 252 232)' },
];

const PromotionalHome = () => {
  const cortex2026KeynoteKey = getCortex2026KeynoteAdvisorKey();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMoving, setIsMoving] = useState(false);
  const [colorIndex, setColorIndex] = useState(0);
  const [colorOrder, setColorOrder] = useState([]);
  const [, setOrderIndex] = useState(0);
  const [isChromium, setIsChromium] = useState(false);
  const [hkImageLoaded, setHkImageLoaded] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  // Detect Chromium-based browsers
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    // Check for Chromium-based browsers (Chrome, Edge, Opera, Brave, etc.)
    // Safari uses WebKit but not Chromium - exclude Safari specifically
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent) && !/edg/.test(userAgent);
    const isChromiumBrowser =
      !isSafari && (
        /chrome/.test(userAgent) ||
        /edg/.test(userAgent) ||
        /opr/.test(userAgent) ||
        /brave/.test(userAgent) ||
        (window.chrome && window.chrome.runtime)
      );

    setIsChromium(isChromiumBrowser);
  }, []);

  // Fallback: fade in hero content if logo doesn't fire onLoad (e.g. cached or error)
  useEffect(() => {
    const fallback = setTimeout(() => setHeroLoaded(true), 2000);
    return () => clearTimeout(fallback);
  }, []);

  // Shuffle array function (Fisher-Yates algorithm)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Initialize random color order
  useEffect(() => {
    const indices = Array.from({ length: PROMOTIONAL_THEME_COLORS.length }, (_, i) => i);
    const shuffled = shuffleArray(indices);
    setColorOrder(shuffled);
    setColorIndex(shuffled[0]);
  }, []);

  useEffect(() => {
    // Only enable mouse tracking on Chromium browsers
    if (!isChromium) return;

    let movementTimeout;

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsMoving(true);

      // Clear any existing timeout
      if (movementTimeout) {
        clearTimeout(movementTimeout);
      }

      // Reset moving state after a delay when mouse stops moving
      movementTimeout = setTimeout(() => {
        setIsMoving(false);
      }, 200);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (movementTimeout) {
        clearTimeout(movementTimeout);
      }
    };
  }, [isChromium]);

  // Color cycling effect with randomized order
  useEffect(() => {
    if (colorOrder.length === 0) return;

    const colorInterval = setInterval(() => {
      setOrderIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % colorOrder.length;

        // If we've completed a full cycle, reshuffle for next time
        if (nextIndex === 0) {
          const indices = Array.from({ length: PROMOTIONAL_THEME_COLORS.length }, (_, i) => i);
          const newOrder = shuffleArray(indices);
          setColorOrder(newOrder);
          setColorIndex(newOrder[0]);
          return 0;
        } else {
          setColorIndex(colorOrder[nextIndex]);
          return nextIndex;
        }
      });
    }, 2000); // Change color every 2 seconds

    return () => clearInterval(colorInterval);
  }, [colorOrder]);

  return (
    <div className="relative min-h-screen bg-white">
      {/* Fixed Hong Kong cover image - full height, fades in when image has loaded */}
      <section
        className={`fixed top-0 left-0 w-full h-screen z-0 transition-opacity duration-700 ease-out ${hkImageLoaded ? 'opacity-100' : 'opacity-0'}`}
      >
        <img
          src="/asset/images/Hong-Kong.png"
          alt="Hong Kong"
          className="w-full h-full object-cover"
          onLoad={() => setHkImageLoaded(true)}
        />
      </section>

      {/* Wrapper so all sections sit above the HK image */}
      <div className="relative z-10">
      {/* Hero Section - content fades in on load */}
      <section className={`relative overflow-hidden transition-all duration-1000 ease-in-out ${PROMOTIONAL_THEME_COLORS[colorIndex].heroBg}`} style={{ minHeight: '100vh' }}>
        <div className="absolute inset-0 pointer-events-none">
          {isChromium && (
            <div
              className={`absolute rounded-full blur-3xl transition-all duration-300 ease-out ${PROMOTIONAL_THEME_COLORS[colorIndex].bg} ${isMoving ? 'w-96 h-96' : 'w-64 h-64'
                }`}
              style={{
                left: mousePosition.x - (isMoving ? 192 : 128),
                top: mousePosition.y - (isMoving ? 192 : 128) - 30,
              }}
            />
          )}
          <div className="absolute -top-16 -left-16 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-16 -right-16 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />
        </div>
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 md:pb-28 relative transition-opacity duration-700 ease-out delay-150 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center mb-0">
              <img
                src="/asset/icons/TITAN_Logo_Original.svg"
                alt="TITAN Logo"
                className="h-40 md:h-56 w-auto"
                onLoad={() => setHeroLoaded(true)}
              />
            </div>
            <div className="-mt-0 mb-8">
              <div className="aspect-video w-full max-w-xl sm:max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-md ring-1 ring-black/5 bg-black/5">
                <iframe
                  src="https://drive.google.com/file/d/1Dt9TClisZAz_wnwRB2uKtANA529VQDHn/preview"
                  className="w-full h-full"
                  allow="autoplay"
                  allowFullScreen
                  title="Mongolian Critical Zone Trip Video"
                />
              </div>
            </div>
            <p className="text-lg md:text-2xl text-gray-700 mt-0 text-center font-bold">Transdisciplinary Initiative for Talents and Innovators</p>
            <p className="text-base md:text-lg text-gray-600 mt-2 text-center">HKU CC Student Learning Partner - Promoting transdisciplinary mindsets and skills</p>
            <div className="mt-4 md:mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/titan"
                className="inline-flex items-center rounded-full bg-white/20 backdrop-blur-sm text-gray-700 px-4 py-2 text-sm md:text-base border border-transparent transition-colors duration-200
                  hover:border-orange-500 hover:text-orange-600"
              >
                Transdisciplinary Learning
              </Link>
              <span className="hidden sm:inline text-gray-400">•</span>
              <Link
                to="/cortex"
                className="inline-flex items-center rounded-full bg-white/20 backdrop-blur-sm text-gray-700 px-4 py-2 text-sm md:text-base border border-transparent transition-colors duration-200
                  hover:border-blue-500 hover:text-blue-600"
              >
                Experiential Research
              </Link>
              <span className="hidden sm:inline text-gray-400">•</span>
              <Link
                to="/jeltr"
                className="inline-flex items-center rounded-full bg-white/20 backdrop-blur-sm text-gray-700 px-4 py-2 text-sm md:text-base border border-transparent transition-colors duration-200
                  hover:border-purple-500 hover:text-purple-600"
              >
                Academic Publishing
              </Link>
            </div>
            {/* <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register" className="px-6 py-3 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold shadow hover:shadow-md transition">
                Get Started
              </Link>
              <Link to="/titan" className={`px-6 py-3 rounded-full border border-gray-700 text-gray-700 font-semibold hover:border-${PROMOTIONAL_THEME_COLORS[colorIndex].name}-600 hover:text-${PROMOTIONAL_THEME_COLORS[colorIndex].name}-600 hover:bg-white/20 transition`}>
                Learn More
              </Link>
            </div> */}
          </div>
        </div>
      </section>

      {/* Event Dashboard Section - posters from siteEvents (CORTEX + Archive) */}
      <section className="w-full bg-white">
        <div className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 items-stretch">
            {getPostersForDashboard().map((poster) => (
              <div key={poster.id} className="w-full h-full min-h-[400px] md:min-h-[500px] bg-white">
                <EventPoster
                  image={poster.image}
                  alt={poster.alt}
                  link={poster.link}
                  fallbackGradient={poster.fallbackGradient}
                  title={poster.title}
                  subtitle={poster.subtitle}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Organizations Section */}
      <AnimatedSection
        className="py-16 md:py-20 bg-white"
        heading={
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Organizations</h2>
            <p className="text-lg text-gray-600 max-w-5xl mx-auto text-center">
              Three interconnected platforms working together to support students, researchers, and educators worldwide.
            </p>
          </div>
        }
        containerClassName="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="group p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 ring-1 ring-orange-200 hover:ring-orange-300 transition shadow-sm">
            <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-sm">
              <img src="/asset/icons/TITAN_Logo_Original.svg" alt="TITAN Logo" className="h-12 w-auto" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">TITAN</h3>
            <p className="text-gray-700 mb-6 text-justify">
              Transdisciplinary Initiative for Talents and Innovators - HKU CC Student Learning Partner providing opportunities for students to experience transdisciplinarity.
            </p>
            <Link to="/titan" className="inline-flex items-center text-orange-600 hover:text-orange-700 font-semibold">
              Learn More
              <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="group p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 ring-1 ring-blue-200 hover:ring-blue-300 transition shadow-sm">
            <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-sm">
              <img src="/asset/icons/CORTEX_Logo_Radial.svg" alt="CORTEX Logo" className="h-12 w-auto" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">CORTEX</h3>
            <p className="text-gray-700 mb-6 text-justify">
              Conference for Research and Transdisciplinary Experiential Learning - A student-led platform under CCSO, HKU promoting transdisciplinary research and experiential learning.
            </p>
            <Link to="/cortex" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold">
              Learn More
              <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="group p-8 rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 ring-1 ring-purple-200 hover:ring-purple-300 transition shadow-sm">
            <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-sm">
              <img src="/asset/icons/JELTR_Logo_Full_Coloured.svg" alt="JELTR Logo" className="h-12 w-auto" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">JELTR</h3>
            <p className="text-gray-700 mb-6 text-justify">
              Journal of Experiential Learning & Transdisciplinary Research - A publication-oriented student body providing opportunities for UG students to formally publish papers.
            </p>
            <Link to="/jeltr" className="inline-flex items-center text-purple-600 hover:text-purple-700 font-semibold">
              Learn More
              <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </AnimatedSection>

      {/* CORTEX Faculty Advisors */}
      <AnimatedSection
      className="py-16 md:py-20 bg-gray-50"
      heading={<h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Faculty Advisors</h2>}
      containerClassName="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div className="bg-white rounded-lg shadow-md p-8">
          <p className="text-lg text-gray-700 leading-relaxed mb-6 text-justify">
            Faculty advisors from HKU and partner institutions support the rigour and quality of CORTEX.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CORTEX_FACULTY_ADVISOR_KEYS.map((advisorKey) => {
              const advisor = getAdvisorsByKeys([advisorKey], 'blue')[0];
              if (!advisor) return null;
              const isKeynote = Boolean(cortex2026KeynoteKey && advisorKey === cortex2026KeynoteKey);
              return (
                <FacultyAdvisorCard
                  key={advisorKey}
                  {...advisor}
                  rimHighlight={isKeynote}
                  footerBadge={isKeynote ? CORTEX_2026_KEYNOTE_FOOTER_BADGE : undefined}
                />
              );
            })}
          </div>
          <div className="mb-6 flex flex-wrap justify-center gap-3">
            <Link to="/cortex/keynote-speech-2026" className={cortexOutlineButtonClass}>
              Keynote Speech 2026 — abstract &amp; bibliography
            </Link>
          </div>
        </div>
      </AnimatedSection>

      {/* Transdisciplinarity overview (from TITAN) */}
      <AnimatedSection
        className="py-16 md:py-20 bg-white"
        heading={
          <h2 className="text-3xl font-semibold text-gray-900 mb-8">
            Transdisciplinarity
          </h2>
        }
        containerClassName="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <TransdisciplinarityOverview withContainer={false} />
      </AnimatedSection>

      {/* News Section - horizontal scroll with arrows */}
      <NewsSectionWithScroll />

      {/* Workshops Section - Commented Out
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Workshops</h2>
            <Link to="/workshops" className="text-blue-600 hover:text-blue-700 font-medium">See all</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-2xl bg-gray-50 ring-1 ring-gray-200 shadow-sm">
              <p className="text-sm text-gray-600">Sat, 20 Sept</p>
              <h3 className="mt-1 text-lg font-semibold text-gray-900">Future Flyers: An Orientation Day of SUA Exploration</h3>
              <p className="mt-2 text-sm text-gray-600">HKU RRST-4.36 & Lung Fu Shan, Pok Fu Lam, Hong Kong</p>
              <div className="mt-4">
                <Link to="/workshops/orientation-day" className="text-blue-600 hover:text-blue-700 font-medium">Register →</Link>
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-gray-50 ring-1 ring-gray-200 shadow-sm">
              <p className="text-sm text-gray-600">Sat, 21 Jun</p>
              <h3 className="mt-1 text-lg font-semibold text-gray-900">Future Flyers: A Hands-On SUA Adventure</h3>
              <p className="mt-2 text-sm text-gray-600">Innovation Wing One, HKU, Pok Fu Lam, Hong Kong</p>
              <p className="mt-1 text-sm text-emerald-600">Low-altitude Economy in action. Learn, Fly, and Master Drones!</p>
              <div className="mt-4">
                <Link to="/workshops/hands-on-sua" className="text-blue-600 hover:text-blue-700 font-medium">Details →</Link>
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-gray-50 ring-1 ring-gray-200 shadow-sm">
              <p className="text-sm text-gray-600">Sat, 22 Mar</p>
              <h3 className="mt-1 text-lg font-semibold text-gray-900">AI Literacy Series: Rise of Neural Network and Deep Learning</h3>
              <p className="mt-2 text-sm text-gray-600">St. Paul's College, 69 Bonham Rd, Mid-Levels, Hong Kong</p>
              <div className="mt-4">
                <Link to="/workshops/ai-literacy" className="text-blue-600 hover:text-blue-700 font-medium">Details →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Keynote Speaker - Commented Out
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Keynote Speaker</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-1">
              <div className="w-full aspect-square rounded-2xl overflow-hidden shadow-lg">
                <img src="/asset/images/javier-cha.jpg" alt="Professor Javier Cha" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="md:col-span-2">
              <h3 className="text-xl md:text-2xl font-semibold text-gray-900">Professor Javier Cha</h3>
              <p className="text-gray-600">Assistant Professor, The University of Hong Kong</p>
              <p className="mt-4 text-gray-700 leading-relaxed text-justify">
                As the keynote speaker for TRACES, Professor Cha exemplifies the conference's commitment to blending humanities, technology, and sustainability. His work aligns with the theme of fostering innovative, transdisciplinary research, inspiring students to explore the future of academic inquiry in a digitally transformed world.
              </p>
              <div className="mt-4">
                <Link to="/keynote/javier-cha" className="text-blue-600 hover:text-blue-700 font-medium">Details →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Timeline - split by year (2026, 2025), reverse chronological within each */}
      <AnimatedSection
        className="py-16 md:py-20 bg-white"
        heading={<h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Timeline</h2>}
        containerClassName="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div className="space-y-8">
          {(() => {
            const currentYear = new Date().getFullYear();
            const cutoffYear = currentYear - 2;
            const yearTagClass = (y) => {
              const yr = Number(y);
              if (yr > currentYear) return 'rounded-lg bg-gray-200 px-3 py-2 inline-block text-gray-700 font-semibold';
              if (yr === currentYear) return 'rounded-lg bg-yellow-100 px-3 py-2 inline-block text-yellow-800 font-semibold';
              return 'rounded-lg bg-green-100 px-3 py-2 inline-block text-green-800 font-semibold';
            };
            return getTimelineEntriesByYear()
              .filter(({ year }) => year >= cutoffYear)
              .map(({ year, entries }) => (
            <div key={year} className="bg-gray-50 ring-1 ring-gray-200 rounded-2xl p-6 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="md:col-span-1">
                  <div className={yearTagClass(year)}>{year}</div>
                </div>
                <div className="space-y-4 md:col-span-4">
                  {entries.map((entry, i) => {
                    const dotColor = entry.accentColor === 'orange' ? 'bg-orange-500' : entry.accentColor === 'purple' ? 'bg-purple-500' : 'bg-blue-500';
                    return (
                      <Link key={`${year}-${entry.path}-${entry.date}-${i}`} to={entry.path} className="flex items-start gap-4 hover:opacity-80 transition">
                        <div className={`w-2 h-2 mt-2 rounded-full shrink-0 ${dotColor}`} />
                        <div>
                          <p className="text-sm text-gray-600">{entry.date}</p>
                          <p className="font-medium text-gray-900">{entry.label}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          ));
          })()}
        </div>
      </AnimatedSection>

      {/* Partners */}
      <AnimatedSection
        className="py-16 md:py-20 bg-gray-50 pb-6"
        heading={<h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10">Our Partners</h2>}
        containerClassName="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div className="space-y-10">
          {/* Row 1: Student Learning Partner + Supporting Organisations */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <section>
              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6">Student Learning Partner with</h3>
              {getPartnersByKeys(STUDENT_LEARNING_PARTNER_KEYS).map((partner) => (
                <PartnerCard
                  key={partner.id}
                  partner={partner}
                  className="p-8 bg-white rounded-2xl w-full md:max-w-sm min-h-[220px]"
                />
              ))}
            </section>
            <section>
              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6">Supporting Organisations</h3>
              {getPartnersByKeys(SUPPORTING_ORG_KEYS).map((partner) => (
                <PartnerCard
                  key={partner.id}
                  partner={partner}
                  className="p-8 bg-white rounded-2xl w-full md:max-w-sm min-h-[220px]"
                />
              ))}
            </section>
          </div>

          {/* Row 2: Mongolian Critical Zone - full width */}
          <section>
            <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6">Mongolian Critical Zone</h3>
            <div className="flex flex-col sm:flex-row sm:items-start gap-8">
              {/* <div>
                <h4 className="text-base md:text-lg font-medium text-gray-700 mb-2">Facilitator</h4>
                <p className="text-lg font-semibold text-gray-900">Yaya Chan</p>
              </div> */}
              <div className="flex-1">
                <h4 className="text-base md:text-lg font-medium text-gray-700 mb-4">Partnering NGOs</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {getPartnersByKeys(MONGOLIA_PARTNER_KEYS).map((partner) => (
                    <PartnerCard
                      key={partner.id}
                      partner={partner}
                      className="p-8 bg-white rounded-2xl w-full md:max-w-sm min-h-[220px]"
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </AnimatedSection>

      {/* Outreach Banner
      <section className="py-14 bg-gradient-to-r from-blue-700 via-indigo-700 to-sky-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h2 className="text-2xl md:text-3xl font-bold text-white">Student Outreach</h2>
            <p className="text-blue-100">Driving research forward</p>
          </div>
        </div>
      </section> */}

      {/* Event CTA
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gray-50 ring-1 ring-gray-200 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-sm">
            <div>
              <p className="text-sm uppercase tracking-wide text-blue-600 font-semibold">TRACES 2025</p>
              <h3 className="mt-1 text-2xl md:text-3xl font-bold text-gray-900">Conference & Presentation</h3>
              <p className="mt-2 text-gray-700 text-justify">28 Jun 2025 • CPD4.36, The University of Hong Kong • Pok Fu Lam, Hong Kong</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/archives" className="px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:border-blue-400 hover:text-blue-600 transition">Read More</Link>
            </div>
          </div>
        </div>
      </section> */}
      </div>
    </div>
  );
};

export default PromotionalHome; 